import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  FormHelperText,
  styled,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as PdfIcon } from "../../../../assets/svg/SmallPdfIcon.svg";
import { toast } from "react-toastify";
import { getApiData, postFormData } from "../../../../utils/APIHelper";
import { useTheme } from "@emotion/react";

const errorObj = {
  documentErr: false,
  documentMsg: "",
};

const modalStyle = (isMobile) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: isMobile ? "95%" : 700,
  maxWidth: isMobile ? "350px" : 700,
  maxHeight: isMobile ? "90vh" : "80vh",
  overflow: "hidden",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: isMobile ? 3 : 4,
  borderRadius: isMobile ? "12px" : 2,
});

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  "&:hover": {
    backgroundColor: "#4D6BF6",
    border: "1px solid #4D6BF6",
  },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
}));

const imageBoxStyle = {
  width: "100%",
  height: "120px",
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#f4f4f4",
  border: "1px solid #D7D8D9",
};

// Helper function to check if item is a PDF
const isPdfFile = (item) => {
  // Check MIME type
  if (item.type === "application/pdf" || item.type === "pdf") {
    return true;
  }
  // Check file extension from URL or filename
  const url = item.image || item.url || item.file || "";
  const filename = item.filename || item.name || "";
  return url.toLowerCase().endsWith(".pdf") || filename.toLowerCase().endsWith(".pdf");
};

const UploadProof = ({
  selectedMilestone,
  open,
  handleClose,
  handleConfirm,
  uploadedImages,
  setUploadedImages,
}) => {
  const [errObj, setErrObj] = useState(errorObj);
  const [uploadLoader, setUploadLoader] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Debug: Track isMobile changes
  useEffect(() => {
    // console.log("isMobile changed to:", isMobile, "Current images count:", uploadedImages.length);
  }, [isMobile, uploadedImages.length]);

  // Debug: Log when images state changes
  useEffect(() => {
    // console.log("Images state changed:", uploadedImages.length, "images:", uploadedImages);
  }, [uploadedImages]);

  // Debug: Log when modal opens/closes
  useEffect(() => {
    // console.log("Modal open state:", open, "isMobile:", isMobile);
    if (open) {
      // console.log("Modal opened with selectedMilestone:", selectedMilestone);
    } else {
      // Reset images when modal closes
      // console.log("Modal closed, resetting images");
      setUploadedImages([]);
      setIsDragOver(false);
    }
  }, [open, selectedMilestone]);

  async function handleFileUpload(files) {
    if (files.length === 0) {
      toast.error("No files selected");
      return;
    }

    // Check if current upload batch exceeds 9 files
    if (files.length > 9) {
      toast.error("You can upload a maximum of 9 files at a time.");
      return;
    }

    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`file_${index + 1}`, file);
    });

    formData.append("type", "milestone");
    formData.append("tags", "Milestone_Proof");
    formData.append("project_id", selectedMilestone?.id);

    setUploadLoader(true);

    try {
      const response = await postFormData("api/project/files", formData);
      if (response.success) {
        // Concatenate previous and new images
        setUploadedImages((prev) => {
          const newImages = [...prev, ...(response.images || [])];
          // console.log("Previous images:", prev.length, "New images:", response.images?.length, "Total:", newImages.length);
          return newImages;
        });
        toast.success("Files uploaded successfully");
      } else {
        toast.error("File upload failed");
      }
    } catch (error) {
      // console.error("Upload error:", error);
      toast.error("An error occurred during file upload");
    } finally {
      setUploadLoader(false);
    }
  }

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDeleteImage = async (id) => {
    setUploadLoader(true);
    try {
      const response = await getApiData(
        `api/project/delete-files/${id}`,
        "POST",
        {},
        { "Content-Type": "application/json" }
      );
      if (response.success) {
        setUploadedImages((prev) => prev.filter((img) => img.id !== id));
        toast.success("File deleted successfully");
      }
    } catch (error) {
      toast.error("Failed to delete file");
    } finally {
      setUploadLoader(false);
    }
  };

  const handleSubmit = () => {
    if (uploadedImages.length === 0) {
      toast.error("Upload proof to submit milestone");
      return;
    }
    // console.log("Submitting with images:", uploadedImages.length, uploadedImages);
    // Pass the uploaded images to the parent component
    handleConfirm(uploadedImages);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle(isMobile)}>
        <Typography
          textAlign={isMobile ? "center" : "left"}
          mb={isMobile ? 2 : 3}
          sx={{
            width: "100%",
            fontSize: "16px",
          }}
        >
          Please upload proofs to submit{" "}
          <b>{selectedMilestone?.milestone_name}</b>
          &nbsp;milestone?
        </Typography>

        {uploadLoader ? (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: isMobile ? 120 : 180 }}
          >
            <CircularProgress color="primary" size={isMobile ? 20 : 26} />
          </Grid>
        ) : (
          <>
            <Box
              sx={{
                position: "relative",
                mb: isMobile ? 1 : 2,
              }}
            >
              <Button
                variant="outlined"
                component="label"
                sx={{
                  fontSize: isMobile ? "14px" : "15px",
                  padding: isMobile ? "8px 16px" : "8px 16px",
                  width: "100%",
                  height: isMobile ? "120px" : "140px",
                  border: isDragOver ? "2px dashed #4D6BF6" : "1px dashed #9CA3AF",
                  backgroundColor: isDragOver ? "rgba(77, 107, 246, 0.05)" : "transparent",
                  transition: "all 0.2s ease-in-out",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": {
                    border: "2px dashed #4D6BF6",
                    backgroundColor: "rgba(77, 107, 246, 0.05)",
                  },
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isMobile ? "12px" : "15px",
                    color: isDragOver ? "#4D6BF6" : "#6B7280",
                    textAlign: "center",
                    fontWeight: isDragOver ? "600" : "600",
                    color: "#4d6bf6"

                  }}
                >
                  {isDragOver ? "Drop files here" : "Click to upload images or drag & drop"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: isMobile ? "10px" : "12px",
                    color: "#6B7280",
                    textAlign: "center",
                  }}
                >
                  PNG, JPG, PDF (max 9 files at a time)
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  hidden
                  onChange={handleFileInputChange}
                />
              </Button>
            </Box>

            {errObj.documentErr && (
              <FormHelperText error={errObj.documentErr}>
                {errObj.documentMsg}
              </FormHelperText>
            )}

            <Grid
              container
              spacing={isMobile ? 1 : 2}
              sx={{
                maxHeight: isMobile ? "40vh" : "30vh",
                overflowY: "scroll",
                overflowX: "hidden",
                paddingRight: isMobile ? "8px" : ".5vw",
              }}
            >
              {uploadedImages.map((item) => (
                <Grid
                  item
                  xs={isMobile ? 6 : 12}
                  sm={6}
                  md={4}
                  key={item.id}
                  sx={{ position: "relative" }}
                >
                  <Box
                    sx={{
                      ...imageBoxStyle,
                      height: isMobile ? "100px" : "120px",
                    }}
                  >
                    {isPdfFile(item) ? (
                      <IconButton
                        onClick={() => window.open(item.image || item.url || item.file, "_blank")}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: "100%",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <PdfIcon
                          style={{
                            width: isMobile ? "40px" : "48px",
                            height: isMobile ? "40px" : "48px",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <img
                        src={item.image}
                        alt="Uploaded"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                    )}
                  </Box>
                  <IconButton
                    onClick={() => handleDeleteImage(item.id)}
                    sx={{
                      position: "absolute",
                      top: isMobile ? "4px" : "8px",
                      right: isMobile ? "-4px" : "-8px",
                      backgroundColor: "rgba(255, 255, 255)",
                      border: "1px solid red",
                      padding: isMobile ? "2px" : "4px",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                      },
                    }}
                  >
                    <CloseIcon
                      sx={{
                        fontSize: isMobile ? "16px" : "20px",
                        color: "red",
                      }}
                    />
                  </IconButton>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: isMobile ? 1 : 2,
                mt: isMobile ? 2 : 3,
              }}
            >
              <GreyStyledButton
                onClick={handleClose}
                sx={{
                  fontSize: isMobile ? "14px" : "15px",
                  padding: "8px 16px",
                }}
              >
                Cancel
              </GreyStyledButton>
              <StyledButton
                onClick={handleSubmit}
                sx={{
                  fontSize: isMobile ? "14px" : "15px",
                  padding: "8px 16px",
                }}
              >
                Submit
              </StyledButton>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default UploadProof;
